import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PhoneLink from '../components/PhoneLink.jsx';
import { toast } from '../lib/toast.js';
import { ACCOUNT_KINDS } from '../lib/accountKinds.js';

// Strategic Accounts directory (Dave 2026-08). A clean, quick-access
// contact directory for critical support groups / partner companies --
// the people XSite needs to reach fast (retail-app support, fuel,
// billing, HQ influencers). Records are contacts with
// record_type='strategic_account'; the deeper record (sub-contacts,
// notes, send-email) lives on the detail page at /strategic-accounts/:id.
//
// This page is deliberately a grouped CARD directory rather than the
// dense data-table the other record types use -- the ask was a "clean
// contact form by company" for at-a-glance lookup. Add is a quick-add
// modal; clicking a card opens the full record.

// Company-monogram colours. Deterministic per company name so the same
// company always gets the same accent -- purely cosmetic. Uses the
// domain / status token hues from theme.css so it reads as one system.
const ACCENT_POOL = [
  'var(--color-accent)', '#7c3aed', '#b45309', '#047857',
  '#b91c1c', '#2563eb', '#0891b2', '#9333ea', '#c2410c'
];

function hashStr(s) {
  let h = 0;
  const str = String(s || '?');
  for (let i = 0; i < str.length; i++) { h = (h << 5) - h + str.charCodeAt(i); h |= 0; }
  return Math.abs(h);
}
function accentFor(company) {
  return ACCENT_POOL[hashStr(company) % ACCENT_POOL.length];
}
function initials(first, last, company) {
  const a = (first || '').trim();
  const b = (last || '').trim();
  if (a || b) return ((a[0] || '') + (b[0] || '')).toUpperCase() || '?';
  const clean = String(company || '').replace(/\b(inc|llc|lp|ltd|corp|co)\b\.?/gi, '').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : clean.slice(0, 2)).toUpperCase() || '?';
}
function companyMono(company) {
  const clean = String(company || '—').replace(/\b(inc|llc|lp|ltd|corp|co)\b\.?/gi, '').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : clean.slice(0, 2)).toUpperCase() || '—';
}
function fullName(r) {
  return [r.first_name, r.last_name].filter(Boolean).join(' ').trim();
}
function addressLine(r) {
  const l1 = [r.address1, r.address2].filter(Boolean).join(', ');
  const l2 = [r.city, r.province_state, r.postal_zip].filter(Boolean).join(' ');
  return [l1, l2].filter(Boolean).join(', ');
}

const EMPTY_ADD = {
  company: '', first_name: '', last_name: '', position: '',
  phone_main: '', phone_cell: '', email1: '',
  address1: '', city: '', province_state: '', postal_zip: '',
  territory: '', notes: ''
};

export default function StrategicAccounts({ kind = 'strategic' }) {
  const K = ACCOUNT_KINDS[kind] || ACCOUNT_KINDS.strategic;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();

  function load() {
    setLoading(true);
    axios.get(K.api)
      .then((r) => { setRows(r.data?.strategic_accounts || []); setError(null); })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [K.api]);

  // Free-text search across every visible field.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.company, r.first_name, r.last_name, r.position, r.organization,
        r.territory, r.phone_main, r.phone_cell, r.email1, r.email2,
        r.city, r.province_state, r.notes
      ].map((v) => (v == null ? '' : String(v).toLowerCase())).join(' | ');
      return hay.includes(q);
    });
  }, [rows, query]);

  // Group the filtered rows by company for the section layout.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of visible) {
      const key = r.company || '— No company —';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([company, items]) => [
        company,
        items.slice().sort((a, b) => fullName(a).localeCompare(fullName(b)))
      ]);
  }, [visible]);

  const companyCount = groups.length;

  return (
    <section className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>{K.title}</h2>
          <div className="subtitle">{K.subtitle}</div>
        </div>
        <button type="button" className="btn-primary" onClick={() => setAddOpen(true)}>
          + Add Contact
        </button>
      </div>

      <div className="toolbar">
        <input
          type="search"
          className="search-input"
          placeholder="Search name, company, role, phone, email, notes…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <span className="result-count">
          {loading ? 'Loading…' : `${visible.length} contact${visible.length === 1 ? '' : 's'} · ${companyCount} compan${companyCount === 1 ? 'y' : 'ies'}`}
        </span>
      </div>

      {error && <p className="error">{error}</p>}

      {!loading && !error && rows.length === 0 && (
        <p className="panel-empty">
          {K.empty} <strong>+ Add Contact</strong>.
        </p>
      )}

      {!loading && !error && rows.length > 0 && visible.length === 0 && (
        <p className="panel-empty">No contacts match “{query}”.</p>
      )}

      {!loading && !error && groups.length > 0 && (
      <div className="sa-card-wrap">
      <div className="sa-group-grid">
      {groups.map(([company, items]) => {
        const accent = accentFor(company);
        const span = Math.min(items.length, 4);
        return (
          <div key={company} className="sa-group" data-span={span}>
            {/* Company section header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', margin: '0 2px var(--space-3)' }}>
              <div style={{
                width: 32, height: 32, borderRadius: 'var(--radius-md)', flexShrink: 0,
                background: accent, color: '#fff', display: 'grid', placeItems: 'center',
                fontFamily: 'var(--font-mono)', fontWeight: 'var(--font-bold)', fontSize: 12, letterSpacing: '0.02em'
              }}>
                {companyMono(company)}
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-large)', fontWeight: 'var(--font-semibold)', letterSpacing: 'var(--tracking-snug)' }}>
                  {company}
                </div>
                <div style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-faint)' }}>
                  {items.length} contact{items.length === 1 ? '' : 's'}
                </div>
              </div>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
            </div>

            {/* Card grid */}
            <div className="sa-card-grid">
              {items.map((r) => (
                <ContactCard key={r.id} r={r} accent={accent} onOpen={() => navigate(`${K.path}/${r.id}`)} />
              ))}
            </div>
          </div>
        );
      })}
      </div>
      </div>
      )}

      {addOpen && (
        <AddContactModal
          K={K}
          onClose={() => setAddOpen(false)}
          onCreated={(id) => { setAddOpen(false); load(); if (id) navigate(`${K.path}/${id}`); }}
        />
      )}
    </section>
  );
}

// ── Card ──────────────────────────────────────────────────────────────
function ContactCard({ r, accent, onOpen }) {
  const name = fullName(r) || '(no name)';
  const addr = addressLine(r);
  // Stop row-navigation when the operator clicks a phone/email link.
  const stop = (e) => e.stopPropagation();
  return (
    <article
      className="panel"
      onClick={onOpen}
      style={{
        position: 'relative', cursor: 'pointer', padding: 'var(--space-4)',
        borderLeft: `3px solid ${accent}`, display: 'flex', flexDirection: 'column',
        gap: 'var(--space-3)'
      }}
      title="Open full record"
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
        <div style={{
          width: 38, height: 38, borderRadius: 'var(--radius-md)', flexShrink: 0,
          background: accent, color: '#fff', display: 'grid', placeItems: 'center',
          fontWeight: 'var(--font-bold)', fontSize: 14
        }}>
          {initials(r.first_name, r.last_name, r.company)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', letterSpacing: 'var(--tracking-snug)' }}>
            {name}
          </div>
          {r.position && (
            <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginTop: 1 }}>
              {r.position}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--text-body)' }}>
        {r.phone_main && (
          <ContactLine label="Office"><PhoneLink number={r.phone_main} /></ContactLine>
        )}
        {r.phone_cell && (
          <ContactLine label="Mobile"><PhoneLink number={r.phone_cell} /></ContactLine>
        )}
        {r.email1 && (
          <ContactLine label="Email">
            <a href={`mailto:${r.email1}`} className="cell-mono" onClick={stop}
               style={{ color: 'inherit', borderBottom: '1px dotted var(--color-text-faint)' }}>
              {r.email1}
            </a>
          </ContactLine>
        )}
        {addr && <ContactLine label="Addr"><span style={{ color: 'var(--color-text-dim)' }}>{addr}</span></ContactLine>}
      </div>

      {r.territory && (
        <div>
          <span className="badge badge-purple">{r.territory}</span>
        </div>
      )}

      {r.notes && (
        <div style={{
          fontSize: 'var(--text-small)', color: 'var(--color-text-dim)',
          background: 'var(--color-surface-sunken)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)', padding: 'var(--space-2) var(--space-3)',
          whiteSpace: 'pre-wrap', lineHeight: 1.5
        }}>
          {r.notes}
        </div>
      )}
    </article>
  );
}

function ContactLine({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
      <span style={{
        fontSize: 'var(--text-caption)', textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-wider)', color: 'var(--color-text-faint)',
        width: 46, flexShrink: 0, fontWeight: 'var(--font-semibold)'
      }}>
        {label}
      </span>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
    </div>
  );
}

// ── Quick-add modal ───────────────────────────────────────────────────
function AddContactModal({ K, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_ADD);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.company.trim() && !form.last_name.trim()) {
      setError('Company or last name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(K.api, {
        company: form.company.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        position: form.position.trim(),
        phone_main: form.phone_main.trim(),
        phone_cell: form.phone_cell.trim(),
        email1: form.email1.trim(),
        address1: form.address1.trim(),
        city: form.city.trim(),
        province_state: form.province_state.trim(),
        postal_zip: form.postal_zip.trim(),
        territory: form.territory.trim(),
        notes: form.notes.trim()
      });
      toast.success('Contact added', `${form.company || form.last_name} added to ${K.title}.`);
      onCreated(res.data?.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label={`Add ${K.single} contact`} style={{ maxWidth: 560 }}>
        <header className="modal-head">
          <h3>Add Contact</h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </header>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
              <label className="form-field" style={{ gridColumn: '1 / -1' }}>
                <span>Company</span>
                <input value={form.company} onChange={set('company')} autoFocus placeholder="e.g. Sunoco LP" />
              </label>
              <label className="form-field">
                <span>First Name</span>
                <input value={form.first_name} onChange={set('first_name')} />
              </label>
              <label className="form-field">
                <span>Last Name</span>
                <input value={form.last_name} onChange={set('last_name')} />
              </label>
              <label className="form-field" style={{ gridColumn: '1 / -1' }}>
                <span>Title / Role</span>
                <input value={form.position} onChange={set('position')} placeholder="e.g. Lead Specialist – Retail App Support" />
              </label>
              <label className="form-field">
                <span>Office Phone</span>
                <input value={form.phone_main} onChange={set('phone_main')} inputMode="tel" className="cell-mono" />
              </label>
              <label className="form-field">
                <span>Mobile</span>
                <input value={form.phone_cell} onChange={set('phone_cell')} inputMode="tel" className="cell-mono" />
              </label>
              <label className="form-field" style={{ gridColumn: '1 / -1' }}>
                <span>Email</span>
                <input type="email" value={form.email1} onChange={set('email1')} className="cell-mono" />
              </label>
              <label className="form-field" style={{ gridColumn: '1 / -1' }}>
                <span>Address</span>
                <input value={form.address1} onChange={set('address1')} placeholder="Street address" />
              </label>
              <label className="form-field">
                <span>City</span>
                <input value={form.city} onChange={set('city')} />
              </label>
              <label className="form-field" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                <span style={{ gridColumn: '1 / -1' }}>Prov / Postal</span>
                <input value={form.province_state} onChange={set('province_state')} placeholder="ON" />
                <input value={form.postal_zip} onChange={set('postal_zip')} className="cell-mono" placeholder="L2R 2N6" />
              </label>
              <label className="form-field">
                <span>Support scope / Territory</span>
                <input value={form.territory} onChange={set('territory')} placeholder="e.g. Retail App Support" />
              </label>
              <label className="form-field" style={{ gridColumn: '1 / -1' }}>
                <span>Notes</span>
                <textarea rows={3} value={form.notes} onChange={set('notes')}
                          placeholder="Escalation path, hours, account #, anything for quick reference…" />
              </label>
            </div>
            {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}
          </div>
          <footer className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Add contact'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
